import { useParams } from 'react-router-dom';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';
import { hasPermission } from '@pec/aion-ui-core/actions/userInfo';
import { UserInfoActivitiesType } from '@pec/aion-ui-core/interfaces/userInfo';
import { useTypedSelector } from '../combineReducers';

const { Organization } = UserInfoActivitiesType;
const { Read, Manage } = ActivityAction;
const {
  Accreditations,
  ActivityDocuments,
  Subscriptions,
  ContractorsQuestionnaire,
  QuestionnaireAdmin
} = ActivityResourceName;

export function usePermission(activityAction = Read) {
  const { userInfo } = useTypedSelector(state => state.userInfo);
  const { organizationId } = useParams<{ organizationId: string }>();

  const hasGlobalPermission = (action: ActivityAction, resourceName: ActivityResourceName) =>
    hasPermission(userInfo, action, resourceName);

  const hasOrganizationPermission = (action: ActivityAction, resourceName: ActivityResourceName) => {
    if (!organizationId) return false;
    return hasPermission(userInfo, action, resourceName, [{ type: Organization, id: organizationId }]);
  };

  const hasAccreditationsPermission =
    hasGlobalPermission(activityAction, Accreditations) || hasOrganizationPermission(activityAction, Accreditations);

  const hasDocumentsPermission =
    hasGlobalPermission(activityAction, ActivityDocuments) ||
    hasOrganizationPermission(activityAction, ActivityDocuments);

  const hasSubscriptionsPermission =
    hasGlobalPermission(activityAction, Subscriptions) || hasOrganizationPermission(activityAction, Subscriptions);

  const hasContractorQuestionnairePermission =
    hasGlobalPermission(activityAction, ContractorsQuestionnaire) ||
    hasOrganizationPermission(activityAction, ContractorsQuestionnaire);

  const hasGlobalQuestionnaireAdminPermission = hasGlobalPermission(Manage, QuestionnaireAdmin);

  return {
    hasAccreditationsPermission,
    hasDocumentsPermission,
    hasSubscriptionsPermission,
    hasGlobalPermission,
    hasOrganizationPermission,
    hasContractorQuestionnairePermission,
    hasGlobalQuestionnaireAdminPermission
  };
}
